// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@vanmeter/XDL/Utilities";
import { mergeWithCss } from "@vanmeter/XDL/Utilities/mergeWithCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    title?: string;
    extendedStyles?: StyledBoxStyles;
    showTopStroke?: boolean;
    [key: string]: any;
}

export interface StyledBoxStyles {
    container?: InjectableCss;
    contentContainer?: InjectableCss;
    topStroke?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const styledBoxStyle = css`
    background: ${getColor("common.white")};
    border: 1px solid ${getColor("common.border")};
    border-radius: 4px;
    display: flex;
    flex-direction: column;
`;

const styledBoxStyles: StyledBoxStyles = {
    container: {
        css: css`
            ${styledBoxStyle}
        `,
    },
    title: {
        variant: "h5",
        forwardAs: "p",
        css: css`
            margin-bottom: 0;
            border-bottom: 1px solid ${color("common.border")};
            width: 100%;
            padding: 12px;
        `,
    },
    topStroke: {
        css: css`
            width: 100%;
            border-top: 4px solid ${getColor("common.ui8")};
        `,
    },
    contentContainer: {
        css: css`
            padding: 12px;
        `,
    },
};

const StyledBox: React.FC<OwnProps> = ({ children, title, showTopStroke, extendedStyles, ...otherProps }) => {
    const [styles] = useState(() => mergeWithCss(styledBoxStyles, extendedStyles));

    if (!React.Children.count(children)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="styled-box" {...otherProps}>
            {showTopStroke && <StyledWrapper {...styles.topStroke} />}
            {title && <Typography {...styles.title}>{title}</Typography>}
            <StyledWrapper {...styles.contentContainer}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default StyledBox;
